import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';
import type { ClassValue } from 'clsx';
import { badgeVariants, type ZardBadgeVariants } from './badge.variants';
import { mergeClasses } from 'src/app/@core/utils/merge-classes';

@Component({
    selector: 'app-badge',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    template: `
    @if (!dot()) {
      {{ content() ?? value() }}
    }
  `,
    host: {
        '[class]': 'classes()',
    },
})
export class BadgeComponent {
    /** Text or numeric label shown inside the badge */
    readonly content = input<string | number>();

    /** Alias for `content` — renders the same label text */
    readonly value = input<string | number>();

    /**
     * Controls badge dimensions.
     * - 'sm'      → compact (h-4)
     * - 'default' → standard (h-5)
     * - 'lg'      → large   (h-6)
     */
    readonly size = input<ZardBadgeVariants['zSize']>('default');

    /**
     * Visual style variant of the badge.
     * Accepts: 'default' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'theme'
     *
     * Use `'theme'` to render the badge with the active CSS theme's background color
     * so it automatically adapts to the current design theme palette.
     *
     * @example
     * <app-badge variant="theme" [content]="count" />
     */
    readonly variant = input<ZardBadgeVariants['zVariant']>('default');

    /** When true, renders the badge as a small dot (hides label, applies dot sizing) */
    readonly dot = input<boolean>(false);

    /** Additional CSS classes to merge onto the host element */
    readonly class = input<ClassValue>('');

    /** Computed host class list — merges CVA variant classes with any extra `class` input */
    protected readonly classes = computed(() =>
        mergeClasses(
            badgeVariants({
                zSize: this.dot() ? undefined : this.size(),
                zVariant: this.variant(),
                zDot: this.dot(),
            }),
            this.class()
        )
    );
}

